import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';
import memberService from '../../services/memberService';
import { addNotification } from '../../store/slices/uiSlice';
import './MemberDetail.css';
import '../MinorMembers/MinorMemberDetail.css';
import { formatDate, formatDateYMD } from '../../utils/date';

// ── Family Graph (shared logic with MinorMemberDetail) ────────────────────────
const PARENT_RELATIONS      = new Set(['父親', '母親', '繼父', '繼母', '養父', '養母', '監護人', '父', '母']);
const UNCLE_AUNT_RELATIONS  = new Set(['伯父', '伯母', '叔父', '叔母', '舅父', '舅母', '姑父', '姑母', '姨父', '姨母']);
const SIBLING_RELATIONS     = new Set(['兄', '姊', '弟', '妹', '堂兄', '堂弟', '堂姊', '堂妹', '表兄', '表弟', '表姊', '表妹']);
const GRANDPARENT_RELATIONS = new Set(['祖父', '祖母', '外祖父', '外祖母', '爺爺', '嫲嫲', '外公', '外婆']);
const CHILD_RELATIONS       = new Set(['兒子', '女兒', '孩子', '子女']);

const TYPE_LABEL = { adult: '成人', children: '兒童', teenage: '少年' };
const TYPE_COLOR = { adult: '#059669', children: '#0ea5e9', teenage: '#7c3aed' };

const FamilyNode = ({ person }) => {
  const typeLabel = person.member_type ? TYPE_LABEL[person.member_type] : null;
  const typeColor = person.member_type ? TYPE_COLOR[person.member_type] : '#6b7280';
  return (
    <div className="family-node" style={{ borderColor: person.is_church_member ? '#16a34a' : '#d1d5db' }}>
      <div className="family-node-relation">{person.relation}</div>
      <div className="family-node-name">{person.chi_name || '—'}</div>
      {person.is_church_member && (
        <>
          <div className="family-node-worship">{person.worship_code || '教會會員'}</div>
          {typeLabel && (
            <div style={{ fontSize: '10px', color: '#fff', background: typeColor,
              borderRadius: '3px', padding: '1px 5px', marginTop: '3px', display: 'inline-block' }}>
              {typeLabel}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const MemberFamilyGraph = ({ member }) => {
  const family = member.family_members || [];
  const selfColor = '#059669'; // green for adult

  if (family.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '64px 24px', color: '#9ca3af' }}>
        <i className="fas fa-users" style={{ fontSize: '3rem', marginBottom: '16px', display: 'block', opacity: 0.3 }}></i>
        <div style={{ fontSize: '15px' }}>暫無家庭成員資料</div>
        <div style={{ fontSize: '13px', marginTop: '6px' }}>請在編輯頁面的「家庭狀況」分頁新增成員</div>
      </div>
    );
  }

  const grandparents    = family.filter(f => GRANDPARENT_RELATIONS.has(f.relation));
  const parents         = family.filter(f => PARENT_RELATIONS.has(f.relation));
  const unclesAunts     = family.filter(f => UNCLE_AUNT_RELATIONS.has(f.relation));
  const olderSiblings   = family.filter(f => ['兄','姊','堂兄','堂姊','表兄','表姊'].includes(f.relation));
  const youngerSiblings = family.filter(f => ['弟','妹','堂弟','堂妹','表弟','表妹'].includes(f.relation));
  const children        = family.filter(f => CHILD_RELATIONS.has(f.relation));
  const others          = family.filter(f =>
    !GRANDPARENT_RELATIONS.has(f.relation) &&
    !PARENT_RELATIONS.has(f.relation) &&
    !UNCLE_AUNT_RELATIONS.has(f.relation) &&
    !SIBLING_RELATIONS.has(f.relation) &&
    !CHILD_RELATIONS.has(f.relation)
  );

  const hasAbove = grandparents.length > 0 || parents.length > 0 || unclesAunts.length > 0;

  return (
    <div className="family-tree">
      {grandparents.length > 0 && (
        <div className="family-level">
          <div className="family-level-label">祖父母 / 外祖父母</div>
          <div className="family-level-nodes">
            {grandparents.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}
      {grandparents.length > 0 && (parents.length > 0 || unclesAunts.length > 0) && <div className="family-connector-v" />}

      {parents.length > 0 && (
        <div className="family-level">
          <div className="family-level-label">父母</div>
          <div className="family-level-nodes">
            {parents.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}

      {unclesAunts.length > 0 && (
        <div className="family-level">
          <div className="family-level-label">伯叔姑舅姨</div>
          <div className="family-level-nodes">
            {unclesAunts.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}

      {hasAbove && <div className="family-connector-v" />}

      {/* Self row with siblings */}
      <div className="family-level">
        <div className="family-level-label">{olderSiblings.length + youngerSiblings.length > 0 ? '兄弟姊妹' : ''}</div>
        <div className="family-level-nodes">
          {olderSiblings.map((p, i) => <FamilyNode key={`elder-${i}`} person={p} />)}
          <div className="family-node family-node-self" style={{ borderColor: selfColor, background: `${selfColor}15` }}>
            <div className="family-node-relation" style={{ background: selfColor, color: '#fff', borderRadius: '4px' }}>本人</div>
            <div className="family-node-worship-id" style={{ color: selfColor }}>{member.worship_id || '—'}</div>
            <div className="family-node-name" style={{ fontWeight: 700 }}>{member.chi_name}</div>
            <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>成人</div>
          </div>
          {youngerSiblings.map((p, i) => <FamilyNode key={`younger-${i}`} person={p} />)}
        </div>
      </div>

      {children.length > 0 && (
        <>
          <div className="family-connector-v" />
          <div className="family-level">
            <div className="family-level-label">子女</div>
            <div className="family-level-nodes">
              {children.map((p, i) => <FamilyNode key={i} person={p} />)}
            </div>
          </div>
        </>
      )}

      {others.length > 0 && (
        <div className="family-others">
          <div className="family-level-label">其他</div>
          <div className="family-level-nodes">
            {others.map((p, i) => <FamilyNode key={i} person={p} />)}
          </div>
        </div>
      )}

      <div className="family-legend">
        <span className="family-legend-item family-legend-church">■ 本教會會員</span>
        <span className="family-legend-item family-legend-other">■ 非教會會員</span>
      </div>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

const MemberDetail = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  
  // HKID visibility state
  const [showFullHKID, setShowFullHKID] = useState(false);
  const [fullHKID, setFullHKID] = useState(null);
  const [loadingHKID, setLoadingHKID] = useState(false);
  
  // Upgrade modal states
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeData, setUpgradeData] = useState({
    member_type: 'associate',
    worship_id: ''
  });
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [activeView, setActiveView] = useState('detail'); // 'detail' | 'family'

  useEffect(() => {
    fetchMemberData();
  }, [id]);

  const fetchMemberData = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await memberService.getMemberById(id);
      setMember(data);
    } catch (err) {
      setError(t('member.detail.failedToLoad'));
      console.error('Error fetching member:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleHKIDVisibility = async () => {
    if (!showFullHKID && !fullHKID) {
      // Load full HKID when showing for the first time
      setLoadingHKID(true);
      try {
        const data = await memberService.getMemberHKID(id);
        setFullHKID(data.hkid);
        setShowFullHKID(true);
      } catch (err) {
        console.error('Error fetching full HKID:', err);
        dispatch(addNotification({
          type: 'error',
          message: t('member.detail.failedToLoadHKID', 'Failed to load HKID')
        }));
      } finally {
        setLoadingHKID(false);
      }
    } else {
      // Just toggle visibility if already loaded
      setShowFullHKID(!showFullHKID);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(t('member.detail.confirmDelete', { name: member.chi_name }))) {
      return;
    }

    try {
      await memberService.deleteMember(id);
      dispatch(addNotification({ type: 'success', title: t('member.detail.successDeleteTitle'), message: t('member.detail.successDeleteMsg') }));
      navigate('/members');
    } catch (err) {
      dispatch(addNotification({ type: 'error', title: t('member.detail.errorDeleteTitle'), message: t('member.detail.errorDeleteMsg') }));
      console.error('Error deleting member:', err);
    }
  };

  const handleUpgradeClick = () => {
    setUpgradeData({
      member_type: member?.member_type || 'regular',
      worship_id: member?.worship_id || ''
    });
    setShowUpgradeModal(true);
  };

  const handleUpgradeChange = (e) => {
    const { name, value } = e.target;
    setUpgradeData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpgradeMember = async () => {
    // Require worship_id for non-temporary member types
    if (upgradeData.member_type !== 'temporary' && !upgradeData.worship_id.trim()) {
      dispatch(addNotification({ 
        type: 'error', 
        title: t('common.errorTitle', { defaultValue: 'Error' }), 
        message: t('member.upgrade.worshipIdRequired', { defaultValue: 'Worship ID is required for this member type' })
      }));
      return;
    }

    try {
      setUpgradeLoading(true);
      
      await api.post('/api/v1/members/admin/upgrade', {
        member_id: member.id,
        member_type: upgradeData.member_type,
        worship_id: upgradeData.worship_id || null
      });

      dispatch(addNotification({ 
        type: 'success', 
        title: t('common.successTitle', { defaultValue: 'Success' }), 
        message: t('member.upgrade.success', { name: member.chi_name, defaultValue: `${member.chi_name} member type updated successfully!` })
      }));
      
      setShowUpgradeModal(false);
      
      // Refresh member data
      fetchMemberData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const safeMsg = Array.isArray(detail)
        ? detail.map(d => d.msg || String(d)).join('; ')
        : (typeof detail === 'string' ? detail : t('member.upgrade.error', { defaultValue: 'Failed to update member type' }));
      dispatch(addNotification({ 
        type: 'error', 
        title: t('common.errorTitle', { defaultValue: 'Error' }), 
        message: safeMsg
      }));
    } finally {
      setUpgradeLoading(false);
    }
  };

  // Use shared date formatter from utils

  const getStatusBadge = (status) => {
    const badgeClass = {
      active: 'badge-success',
      inactive: 'badge-secondary',
      suspended: 'badge-warning'
    };
    return badgeClass[status] || 'badge-secondary';
  };

  const getStatusLabel = (status) => {
    const map = {
      active: t('member.detail.statusActive'),
      inactive: t('member.detail.statusInactive'),
      suspended: t('member.detail.statusSuspended'),
    };
    return map[status] || status || t('common.na');
  };

  const getGenderLabel = (sex) => {
    if (!sex) return t('common.na');
    const key = String(sex).trim().toLowerCase();
    if (key === 'male' || key === '男' || key === 'm') return t('member.form.options.genderMale');
    if (key === 'female' || key === '女' || key === 'f') return t('member.form.options.genderFemale');
    if (key === 'other' || key === '其他') return t('member.form.options.genderOther');
    return sex;
  };

  const getMemberRoleLabel = (memberType) => {
    const map = {
      regular: t('member.detail.roleRegular'),
      associate: t('member.detail.roleAssociate'),
      temporary: t('member.detail.roleTemporary'),
    };
    return map[memberType] || memberType || t('common.na');
  };

  const formatAddress = (m) => {
    const parts = [m.new_region, m.addr_district_name, m.street, m.estate, m.building, m.new_number]
      .map((s) => (s || '').trim())
      .filter(Boolean);
    if (parts.length) return parts.join(' ');
    return (m.addr2 || '').trim() || t('common.na');
  };

  if (loading) {
    return (
      <div className="member-detail-container">
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="sr-only">{t('loading.loading')}</span>
          </div>
          <p>{t('member.detail.loadingData')}</p>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="member-detail-container">
        <div className="alert alert-danger" role="alert">
          {error || t('member.detail.notFound')}
          <div className="mt-3">
            <Link to="/members" className="btn btn-primary">
              {t('member.detail.backToList')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="member-detail-container">
      <div className="member-detail-header">
        <div className="header-left">
          <Link to="/members" className="btn-back">
            <i className="fas fa-arrow-left"></i> {t('member.detail.backShort')}
          </Link>
          <div className="header-titles">
            <div className="member-breadcrumb">
              <Link to="/members">{t('member.detail.breadcrumb')}</Link>
              <span className="breadcrumb-sep"> / </span>
              <span>{member.chi_name}</span>
            </div>
            <h2>{member.chi_name}</h2>
          </div>
        </div>
        <div className="header-actions">
          {/* Change Member Type button - show for all members */}
          {member && (
            <button onClick={handleUpgradeClick} className="btn btn-success">
              <i className="fas fa-exchange-alt"></i> {t('member.upgrade.button')}
            </button>
          )}
          <Link to={`/members/${id}/edit`} className="btn btn-warning">
            <i className="fas fa-edit"></i> {t('common.edit')}
          </Link>
          <button onClick={handleDelete} className="btn btn-danger">
            <i className="fas fa-trash"></i> {t('common.delete')}
          </button>
        </div>
      </div>

      <div className="member-detail-card">
        {/* Profile Section */}
        <div className="profile-section">
          {member.photo_url ? (
            <img src={member.photo_url} alt={member.chi_name} className="profile-photo" />
          ) : (
            <div className="profile-photo-placeholder">
              <i className="fas fa-user"></i>
            </div>
          )}
          <div className="profile-info">
            <h3>{member.chi_name}</h3>
            {member.eng_name && <p className="english-name">{member.eng_name}</p>}
            {member.nickname && (
              <p className="member-nickname">{t('member.detail.nickname')} {member.nickname}</p>
            )}
            <span className={`badge ${getStatusBadge(member.status_name)}`}>
              {getStatusLabel(member.status_name)}
            </span>
            <span className="badge badge-info member-role-badge">
              {getMemberRoleLabel(member.member_type)}
            </span>
            <p className="member-number">
              {t('member.detail.worshipNo')} {member.worship_id || t('common.na')}
            </p>
            <p className="member-org-line member-org-group">
              <span className="member-org-label">{t('member.detail.belongingGroup')}</span>
              <span className="member-org-value member-groups-text" title={member.groups || ''}>
                {member.org_leadership === 'zone_leader' || member.org_leadership === 'district_pastor'
                  ? 'NA'
                  : ((member.groups || '').trim() || t('common.na'))}
              </span>
            </p>
            <p className="member-org-line">
              <span className="member-org-label">{t('member.detail.belongingZone')}</span>
              <span className="member-org-value">
                {member.org_leadership === 'district_pastor'
                  ? 'NA'
                  : ((member.zones || '').trim() || t('common.na'))}
              </span>
            </p>
            <p className="member-org-line">
              <span className="member-org-label">{t('member.detail.belongingDistrict')}</span>
              <span className="member-org-value">{(member.districts || '').trim() || t('common.na')}</span>
            </p>
            <p className="member-org-line">
              <span className="member-org-label">{t('member.detail.groupRole')}</span>
              <span className="member-org-value">
                {member.org_leadership === 'zone_leader' || member.org_leadership === 'district_pastor'
                  ? 'NA'
                  : (({
                      core: t('organization.membersModal.roleCore', { defaultValue: '核心組員' }),
                      full: t('organization.membersModal.roleFull', { defaultValue: '正式組員' }),
                      associate: t('organization.membersModal.roleAssociate', { defaultValue: '候任組員' }),
                    })[member.group_role] || t('common.na'))}
              </span>
            </p>
          </div>
        </div>

        {/* View toggle tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '2px solid #e5e7eb', paddingBottom: '0' }}>
          <button
            type="button"
            onClick={() => setActiveView('detail')}
            style={{
              padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: activeView === 'detail' ? 700 : 400,
              borderBottom: activeView === 'detail' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeView === 'detail' ? '#2563eb' : '#6b7280',
              marginBottom: '-2px', fontSize: '14px',
            }}
          >
            <i className="fas fa-id-card" style={{ marginRight: 6 }}></i>詳細資料
          </button>
          <button
            type="button"
            onClick={() => setActiveView('family')}
            style={{
              padding: '8px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: activeView === 'family' ? 700 : 400,
              borderBottom: activeView === 'family' ? '2px solid #2563eb' : '2px solid transparent',
              color: activeView === 'family' ? '#2563eb' : '#6b7280',
              marginBottom: '-2px', fontSize: '14px',
            }}
          >
            <i className="fas fa-sitemap" style={{ marginRight: 6 }}></i>家庭關係圖
            {member.family_members && member.family_members.length > 0 && (
              <span style={{ marginLeft: 6, background: '#2563eb', color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px' }}>
                {member.family_members.length}
              </span>
            )}
          </button>
        </div>

        {activeView === 'family' && (
          <MemberFamilyGraph member={member} />
        )}

        {activeView === 'detail' && (
        <>
        {/* Information Sections */}
        <div className="info-grid">
          {/* Personal Information */}
          <div className="info-section">
            <h4>{t('member.detail.personalInfo')}</h4>
            <div className="info-row">
              <span className="info-label">{t('member.detail.nickname')}</span>
              <span className="info-value">{member.nickname || t('common.na')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.gender')}</span>
              <span className="info-value">{getGenderLabel(member.sex_name)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.dob')}</span>
              <span className="info-value">{formatDateYMD(member.date_of_birth)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.idNumber')}</span>
              <span className="info-value">
                {member.display_hkid || member.hkid || t('common.na')}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.maritalStatus')}</span>
              <span className="info-value">{member.married_status_name || t('common.na')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.occupation')}</span>
              <span className="info-value">{member.job || t('common.na')}</span>
            </div>
          </div>

          {/* Contact Information */}
          <div className="info-section">
            <h4>{t('member.detail.contactInfo')}</h4>
            <div className="info-row">
              <span className="info-label">{t('member.detail.mobile')}</span>
              <span className="info-value">
                {(member.mobile || member.tel_day) ? (
                  <a href={`tel:${member.mobile || member.tel_day}`}>{member.mobile || member.tel_day}</a>
                ) : (
                  t('common.na')
                )}
              </span>
            </div>
            <div className="info-row info-row-email">
              <span className="info-label">{t('member.detail.email')}</span>
              <span className="info-value info-value-email">
                {(member.email || '').trim() ? (
                  <a href={`mailto:${member.email.trim()}`}>{member.email.trim()}</a>
                ) : (
                  t('common.na')
                )}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.address')}</span>
              <span className="info-value">{formatAddress(member)}</span>
            </div>
          </div>

          {/* Membership Information */}
          <div className="info-section">
            <h4>{t('member.detail.membershipInfo')}</h4>
            <div className="info-row">
              <span className="info-label">{t('member.detail.memberRole')}</span>
              <span className="info-value">{getMemberRoleLabel(member.member_type)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.membershipType')}</span>
              <span className="info-value">{getStatusLabel(member.status_name)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.joinDate')}</span>
              <span className="info-value">{formatDate(member.date_of_join)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.transferDate')}</span>
              <span className="info-value">{formatDate(member.date_of_transfer_add)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.baptismDate')}</span>
              <span className="info-value">{formatDate(member.date_of_baptism)}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.baptismPlace')}</span>
              <span className="info-value">{member.baptism_church || t('common.na')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.baptismNo')}</span>
              <span className="info-value">{member.baptism_no || t('common.na')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.baptistPriest')}</span>
              <span className="info-value">{member.baptist_priest || t('common.na')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.baptismClass')}</span>
              <span className="info-value">{member.baptism_appointed_date || t('common.na')}</span>
            </div>
          </div>

          {/* Serve Positions */}
          <div className="info-section">
            <h4>事奉崗位</h4>
            <div className="info-row">
              <span className="info-label">事奉崗位</span>
              <span className="info-value">
                {member.serve_post_items
                  ? member.serve_post_items.split(',').map((s, i) => (
                      <span key={i} style={{ display: 'inline-block', background: '#eff6ff', color: '#1e40af', borderRadius: 4, padding: '2px 8px', marginRight: 6, marginBottom: 4, fontSize: 13, fontWeight: 500 }}>
                        {s.trim()}
                      </span>
                    ))
                  : t('common.na')}
              </span>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="info-section">
            <h4>{t('member.detail.emergencyContact')}</h4>
            <div className="info-row">
              <span className="info-label">{t('member.detail.contactName')}</span>
              <span className="info-value">{member.emergency_contact_person || t('common.na')}</span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.contactPhone')}</span>
              <span className="info-value">
                {member.emergency_contact_tel ? (
                  <a href={`tel:${member.emergency_contact_tel}`}>
                    {member.emergency_contact_tel}
                  </a>
                ) : (
                  t('common.na')
                )}
              </span>
            </div>
            <div className="info-row">
              <span className="info-label">{t('member.detail.relationship')}</span>
              <span className="info-value">{member.emergency_contact_relation_name || t('common.na')}</span>
            </div>
          </div>
        </div>

        {/* Notes Section */}
        {member.remark && (
          <div className="notes-section">
            <h4>{t('member.detail.additionalNotes')}</h4>
            <p>{member.remark}</p>
          </div>
        )}

        {/* System Information */}
        <div className="system-info">
          <small>
            {t('member.detail.created')} {formatDate(member.create_date_time)}
            {(member.modify_date_time || member.last_modified_date_time) &&
              ` | ${t('member.detail.lastUpdated')} ${formatDate(member.modify_date_time || member.last_modified_date_time)}`}
          </small>
        </div>
        </>
        )}
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <div className="modal-overlay" onClick={() => setShowUpgradeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{t('member.upgrade.title', { name: member.chi_name })}</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowUpgradeModal(false)}
                disabled={upgradeLoading}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="member_type">{t('member.upgrade.memberType', { defaultValue: 'Member Type' })}</label>
                <select
                  id="member_type"
                  name="member_type"
                  className="form-control"
                  value={upgradeData.member_type}
                  onChange={handleUpgradeChange}
                  disabled={upgradeLoading}
                >
                  <option value="regular">{t('member.tabs.fullMember', { defaultValue: 'Full Member' })}</option>
                  <option value="associate">{t('member.tabs.associate', { defaultValue: 'Associate Member' })}</option>
                  <option value="temporary">{t('member.tabs.temporary', { defaultValue: 'Temporary Member' })}</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="worship_id">
                  {t('member.upgrade.worshipId', { defaultValue: 'Worship Number' })}
                  {upgradeData.member_type !== 'temporary' && <span className="text-danger"> *</span>}
                </label>
                <input
                  type="text"
                  id="worship_id"
                  name="worship_id"
                  className="form-control"
                  value={upgradeData.worship_id}
                  onChange={handleUpgradeChange}
                  placeholder={t('member.upgrade.worshipIdPlaceholder', { defaultValue: 'Enter worship number' })}
                  disabled={upgradeLoading}
                  required={upgradeData.member_type !== 'temporary'}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowUpgradeModal(false)}
                disabled={upgradeLoading}
              >
                {t('common.cancel', { defaultValue: 'Cancel' })}
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpgradeMember}
                disabled={upgradeLoading}
              >
                {upgradeLoading ? t('member.upgrade.upgrading', { defaultValue: 'Updating...' }) : t('member.upgrade.confirm', { defaultValue: 'Confirm' })}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MemberDetail;
